<template>
    <div ref="rootElement" class="cadastral-map-layer" style="display: none;"></div>
</template>

<script lang="ts">
import {log} from '@/model/logger';
import {bindInemComponents} from '@/naja/components';
import {acquireLeafletMap, AcquiredMap} from '@/shared/leafletMapRegistry';
import {registerMinZoom, unregisterMinZooms} from '@/shared/layersControlMinZoom';
import {Modal} from 'bootstrap';
import L from 'leaflet';
import {defineComponent, nextTick, onBeforeUnmount, onMounted, ref} from 'vue';

interface Parcel {
    readonly id: string;
    readonly number: string;
    readonly geometry: GeoJSON.Geometry;
}

interface BuildingObject {
    readonly id: string;
    readonly houseNumber: number | null;
    readonly geometry: GeoJSON.Geometry;
}

interface BuildingObjectDetail {
    readonly id: string;
    readonly centerPoint: {readonly latitude: number; readonly longitude: number} | null;
    readonly geometry: GeoJSON.Geometry | null;
    readonly cadastralUnitId: string | null;
    readonly houseNumber: number | null;
    readonly completionDate: string | null;
    readonly construction: string | null;
    readonly numberOfFloors: number | null;
    readonly numberOfFlats: number | null;
    readonly sewage: string | null;
    readonly gas: string | null;
    readonly water: string | null;
    readonly heating: string | null;
    readonly floorArea: number | null;
    readonly buildUpArea: number | null;
    readonly enclosedArea: number | null;
    readonly elevator: boolean | null;
}

interface ParcelDetail {
    readonly id: string;
    readonly number: string;
    readonly area: number;
    readonly landType: string;
    readonly addressPlaces: ParcelDetailAddressPlace[];
}

interface ParcelDetailAddressPlace {
    readonly houseNumber: number | null;
    readonly evidenceNumber: number | null;
    readonly estateType: string | null;
    readonly constructionDate: string | null;
    readonly construction: string | null;
    readonly numberOfFloors: number | null;
    readonly sewage: string | null;
    readonly gas: string | null;
    readonly water: string | null;
    readonly heating: string | null;
    readonly floorArea: number | null;
    readonly buildUpArea: number | null;
    readonly completionDate: string | null;
}

interface BoundingBoxRequest {
    readonly boundingBox: {
        readonly southWest: {
            readonly longitude: number;
            readonly latitude: number;
        };
        readonly northEast: {
            readonly longitude: number;
            readonly latitude: number;
        };
    };
}

interface PopupSection {
    type: 'header' | 'subheader';
    title: string;
    rows: Array<{label: string; value: string}>;
}
type PopupSections = PopupSection[];

const MIN_ZOOM = 18;
const LABEL_MIN_ZOOM = 19;
const MAX_CACHED_ITEMS = 1500;
const DEBOUNCE_MS = 500;

const PARCEL_STYLE: L.PathOptions = {
    color: '#333333',
    weight: 0.8,
    fillColor: '#f5e6c8',
    fillOpacity: 0.25,
};

const BUILDING_STYLE: L.PathOptions = {
    color: '#555555',
    weight: 1,
    fillColor: '#999999',
    fillOpacity: 0.45,
};

const HIGHLIGHT_STYLE: L.PathOptions = {
    color: '#1a73e8',
    weight: 2.5,
    fillColor: '#4a90d9',
    fillOpacity: 0.35,
};

export default defineComponent({
    props: {
        loadParcelsUrl: {
            type: String,
            required: true,
        },
        loadBuildingObjectsUrl: {
            type: String,
            required: true,
        },
        loadBuildingObjectDetailUrl: {
            type: String,
            required: true,
        },
        loadParcelDetailUrl: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const rootElement = ref<HTMLElement | null>(null);

        let map: L.Map | null = null;
        let parcelsLayerGroup: L.LayerGroup | null = null;
        let buildingsLayerGroup: L.LayerGroup | null = null;
        let parcelsLayer: L.GeoJSON | null = null;
        let buildingsLayer: L.GeoJSON | null = null;

        const parcelsCache: Map<string, Parcel> = new Map();
        const buildingsCache: Map<string, BuildingObject> = new Map();
        const detailCache: Map<string, BuildingObjectDetail> = new Map();
        const parcelDetailCache: Map<string, ParcelDetail> = new Map();
        let loadedBounds: L.LatLngBounds[] = [];

        let previousAbortController: AbortController | null = null;
        let debounceTimer: number | null = null;
        let boundOnMapMoveEnd: (() => void) | null = null;
        let boundOnMapClick: ((e: L.LeafletMouseEvent) => void) | null = null;
        let zoomHintControl: L.Control | null = null;
        let highlightedLayer: L.Path | null = null;
        let highlightedFeatureId: string | null = null;
        let highlightedFeatureType: 'parcel' | 'building' | null = null;

        function attachToMap(acquired: AcquiredMap): void {
            map = acquired.map;
            acquiredLayersControl = acquired.layersControl;
            initLayers();
            boundOnMapMoveEnd = handleMapMoveEnd.bind(null);
            map.on('moveend', boundOnMapMoveEnd);
            handleMapMoveEnd();
        }

        let acquiredLayersControl: L.Control.Layers | null = null;

        function findLayersControl(): L.Control.Layers | null {
            return acquiredLayersControl;
        }

        function isParcelsVisible(): boolean {
            return map !== null && parcelsLayerGroup !== null && map.hasLayer(parcelsLayerGroup);
        }

        function isBuildingsVisible(): boolean {
            return map !== null && buildingsLayerGroup !== null && map.hasLayer(buildingsLayerGroup);
        }

        const onOverlayToggle = (): void => {
            nextTick(() => {
                updateZoomHint();
                updateCursorClass();

                if (isParcelsVisible() || isBuildingsVisible()) {
                    loadData();
                }
            });
        };

        function initLayers(): void {
            if (!map) {
                return;
            }

            const cadastralPane = map.createPane('cadastral');
            cadastralPane.style.zIndex = '300';
            cadastralPane.style.pointerEvents = 'none';

            const labelsPane = map.createPane('cadastralLabels');
            labelsPane.style.zIndex = '310';
            labelsPane.style.pointerEvents = 'none';

            injectLabelStyles();

            const cadastralRenderer = L.svg({pane: 'cadastral'});

            parcelsLayer = L.geoJSON(undefined, {
                pane: 'cadastral',
                interactive: false,
                style: () => ({...PARCEL_STYLE}),
                onEachFeature: (feature, layer) => {
                    if (feature.properties?.number) {
                        (layer as L.Path).bindTooltip(feature.properties.number, {
                            permanent: true,
                            direction: 'center',
                            className: 'cadastral-label cadastral-label--parcel',
                            pane: 'cadastralLabels',
                        });
                    }
                },
                ...{smoothFactor: 0},
            } as L.GeoJSONOptions);
            buildingsLayer = L.geoJSON(undefined, {
                pane: 'cadastral',
                interactive: false,
                style: () => ({...BUILDING_STYLE}),
                onEachFeature: (feature, layer) => {
                    if (feature.properties?.houseNumber != null) {
                        (layer as L.Path).bindTooltip(String(feature.properties.houseNumber), {
                            permanent: true,
                            direction: 'center',
                            className: 'cadastral-label cadastral-label--building',
                            pane: 'cadastralLabels',
                        });
                    }
                },
                ...{smoothFactor: 0},
            } as L.GeoJSONOptions);

            (parcelsLayer as any).options.renderer = cadastralRenderer;
            (buildingsLayer as any).options.renderer = cadastralRenderer;

            parcelsLayerGroup = L.layerGroup([parcelsLayer]);
            buildingsLayerGroup = L.layerGroup([buildingsLayer]);

            const layersControl = findLayersControl();

            if (layersControl) {
                layersControl.addOverlay(parcelsLayerGroup, 'Parcely');
                layersControl.addOverlay(buildingsLayerGroup, 'Budovy');
                registerMinZoom(map, layersControl, parcelsLayerGroup, MIN_ZOOM);
                registerMinZoom(map, layersControl, buildingsLayerGroup, MIN_ZOOM);
            }

            zoomHintControl = new L.Control({position: 'bottomleft'});
            zoomHintControl.onAdd = () => {
                const div = L.DomUtil.create('div', 'leaflet-bar cadastral-zoom-hint');
                div.innerHTML = 'Pro zobrazení katastru přibližte mapu';
                return div;
            };
            zoomHintControl.addTo(map);
            updateZoomHint();

            boundOnMapClick = handleMapClick.bind(null);
            map.on('click', boundOnMapClick);

            map.on('overlayadd', onOverlayToggle);
            map.on('overlayremove', onOverlayToggle);
        }

        function detachFromMap(): void {
            if (acquiredLayersControl) {
                unregisterMinZooms(
                    acquiredLayersControl,
                    [parcelsLayerGroup, buildingsLayerGroup].filter((layer): layer is L.LayerGroup => layer !== null),
                );
            }

            if (map) {
                if (boundOnMapMoveEnd) {
                    map.off('moveend', boundOnMapMoveEnd);
                }
                if (boundOnMapClick) {
                    map.off('click', boundOnMapClick);
                }
                map.off('overlayadd', onOverlayToggle);
                map.off('overlayremove', onOverlayToggle);

                if (parcelsLayerGroup) {
                    map.removeLayer(parcelsLayerGroup);
                }
                if (buildingsLayerGroup) {
                    map.removeLayer(buildingsLayerGroup);
                }
                if (zoomHintControl) {
                    map.removeControl(zoomHintControl);
                }
                map.getContainer().classList.remove('cadastral-active');
            }

            parcelsLayerGroup = null;
            buildingsLayerGroup = null;
            parcelsLayer = null;
            buildingsLayer = null;
            zoomHintControl = null;
            acquiredLayersControl = null;
            map = null;
        }

        function handleMapMoveEnd(): void {
            if (debounceTimer !== null) {
                window.clearTimeout(debounceTimer);
            }

            updateZoomHint();
            updateCursorClass();

            if (!map || (!isParcelsVisible() && !isBuildingsVisible())) {
                return;
            }

            const currentBounds = map.getBounds();
            if (!currentBounds || !currentBounds.isValid()) {
                return;
            }

            if (map.getZoom() < MIN_ZOOM) {
                clearLayers();
                return;
            }

            refreshLayers();

            if (isAreaLoaded(currentBounds)) {
                return;
            }

            debounceTimer = window.setTimeout(() => {
                loadData();
            }, DEBOUNCE_MS);
        }

        async function loadData(): Promise<void> {
            if (!map) {
                return;
            }

            const abortController = new AbortController();
            previousAbortController?.abort();
            previousAbortController = abortController;

            const bounds = map.getBounds();

            if (!bounds || !bounds.isValid() || map.getZoom() < MIN_ZOOM) {
                return;
            }

            const requestData = buildRequestData(bounds);

            try {
                const promises: [Promise<Parcel[]>, Promise<BuildingObject[]>] = [
                    isParcelsVisible()
                        ? fetchParcels(requestData, abortController.signal)
                        : Promise.resolve([]),
                    isBuildingsVisible()
                        ? fetchBuildingObjects(requestData, abortController.signal)
                        : Promise.resolve([]),
                ];

                const [fetchedParcels, buildings] = await Promise.all(promises);

                if (abortController.signal.aborted) {
                    return;
                }

                evictCacheIfNeeded(fetchedParcels.length + buildings.length);
                addParcelsToCache(fetchedParcels);
                addBuildingsToCache(buildings);
                expandLoadedBounds(bounds);
                refreshLayers();
            } catch (error) {
                if (!abortController.signal.aborted) {
                    log(error instanceof Error ? error.message : String(error));
                }
            }
        }

        function buildRequestData(bounds: L.LatLngBounds): BoundingBoxRequest {
            return {
                boundingBox: {
                    southWest: {
                        longitude: bounds.getSouthWest().lng,
                        latitude: bounds.getSouthWest().lat,
                    },
                    northEast: {
                        longitude: bounds.getNorthEast().lng,
                        latitude: bounds.getNorthEast().lat,
                    },
                },
            };
        }

        async function fetchParcels(
            requestData: BoundingBoxRequest, signal: AbortSignal,
        ): Promise<Parcel[]> {
            const response = await fetch(props.loadParcelsUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(requestData),
                signal,
            });

            if (!response.ok) {
                throw new Error(`Parcels fetch failed with status ${response.status}`);
            }

            return response.json();
        }

        async function fetchBuildingObjects(
            requestData: BoundingBoxRequest, signal: AbortSignal,
        ): Promise<BuildingObject[]> {
            const response = await fetch(props.loadBuildingObjectsUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(requestData),
                signal,
            });

            if (!response.ok) {
                throw new Error(`Building objects fetch failed with status ${response.status}`);
            }

            return response.json();
        }

        async function fetchBuildingObjectDetail(id: string): Promise<BuildingObjectDetail> {
            const cached = detailCache.get(id);

            if (cached) {
                return cached;
            }

            const url = props.loadBuildingObjectDetailUrl.replace('__ID__', encodeURIComponent(id));
            const response = await fetch(url, {
                method: 'GET',
                headers: {Accept: 'application/json'},
            });

            if (!response.ok) {
                throw new Error(`Building object detail fetch failed with status ${response.status}`);
            }

            const detail: BuildingObjectDetail = await response.json();
            detailCache.set(id, detail);
            return detail;
        }

        async function fetchParcelDetail(id: string): Promise<ParcelDetail> {
            const cached = parcelDetailCache.get(id);

            if (cached) {
                return cached;
            }

            const url = props.loadParcelDetailUrl.replace('__ID__', encodeURIComponent(id));
            const response = await fetch(url, {
                method: 'GET',
                headers: {Accept: 'application/json'},
            });

            if (!response.ok) {
                throw new Error(`Parcel detail fetch failed with status ${response.status}`);
            }

            const detail: ParcelDetail = await response.json();
            parcelDetailCache.set(id, detail);
            return detail;
        }

        function addParcelsToCache(parcels: Parcel[]): void {
            for (const parcel of parcels) {
                parcelsCache.set(parcel.id, parcel);
            }
        }

        function addBuildingsToCache(buildings: BuildingObject[]): void {
            for (const building of buildings) {
                buildingsCache.set(building.id, building);
            }
        }

        function evictCacheIfNeeded(incomingCount: number): void {
            const totalCached = parcelsCache.size + buildingsCache.size;

            if (totalCached + incomingCount > MAX_CACHED_ITEMS) {
                parcelsCache.clear();
                buildingsCache.clear();
                loadedBounds = [];
            }
        }

        function isAreaLoaded(bounds: L.LatLngBounds): boolean {
            return loadedBounds.some((lb) => lb.contains(bounds));
        }

        function expandLoadedBounds(newBounds: L.LatLngBounds): void {
            const margin = 0.001;
            const newBoundsObj = L.latLngBounds(
                newBounds.getSouthWest(),
                newBounds.getNorthEast(),
            );

            for (const existingBounds of loadedBounds) {
                const extended = L.latLngBounds(
                    L.latLng(existingBounds.getSouth() - margin, existingBounds.getWest() - margin),
                    L.latLng(existingBounds.getNorth() + margin, existingBounds.getEast() + margin),
                );

                if (extended.intersects(newBoundsObj)) {
                    existingBounds.extend(newBoundsObj);
                    return;
                }
            }

            loadedBounds.push(newBoundsObj);
        }

        function refreshLayers(): void {
            if (!parcelsLayer || !buildingsLayer || !map) {
                return;
            }

            parcelsLayer.clearLayers();
            buildingsLayer.clearLayers();

            const mapBounds = map.getBounds();
            const showLabels = map.getZoom() >= LABEL_MIN_ZOOM;

            const visibleParcels = Array.from(parcelsCache.values()).filter((parcel) => {
                const bounds = getGeometryBounds(parcel.geometry);
                return bounds !== null && mapBounds.intersects(bounds);
            });
            const visibleBuildings = Array.from(buildingsCache.values()).filter((building) => {
                const bounds = getGeometryBounds(building.geometry);
                return bounds !== null && mapBounds.intersects(bounds);
            });

            const buildingLabelPoints: L.Point[] = [];
            if (showLabels && isBuildingsVisible()) {
                for (const building of visibleBuildings) {
                    if (building.houseNumber != null) {
                        const bounds = getGeometryBounds(building.geometry);
                        if (bounds) {
                            buildingLabelPoints.push(map.latLngToContainerPoint(bounds.getCenter()));
                        }
                    }
                }
            }

            const parcelsCollection: GeoJSON.FeatureCollection = {
                type: 'FeatureCollection',
                features: visibleParcels.map((parcel) => {
                    let parcelNumber: string | null = null;

                    if (showLabels) {
                        parcelNumber = parcel.number;
                        if (buildingLabelPoints.length > 0) {
                            const parcelBounds = getGeometryBounds(parcel.geometry);
                            if (parcelBounds) {
                                const pp = map!.latLngToContainerPoint(parcelBounds.getCenter());
                                if (buildingLabelPoints.some((bp) =>
                                    Math.abs(pp.x - bp.x) < 20 && Math.abs(pp.y - bp.y) < 12)) {
                                    parcelNumber = null;
                                }
                            }
                        }
                    }

                    return {
                        type: 'Feature' as const,
                        id: parcel.id,
                        geometry: parcel.geometry,
                        properties: {
                            id: parcel.id,
                            number: parcelNumber,
                            type: 'parcel',
                        },
                    };
                }),
            };

            const buildingsCollection: GeoJSON.FeatureCollection = {
                type: 'FeatureCollection',
                features: Array.from(buildingsCache.values()).map((building) => ({
                    type: 'Feature' as const,
                    id: building.id,
                    geometry: building.geometry,
                    properties: {
                        id: building.id,
                        houseNumber: showLabels ? building.houseNumber : null,
                        type: 'building',
                    },
                })),
            };

            parcelsLayer.addData(parcelsCollection);
            buildingsLayer.addData(buildingsCollection);
            reapplyHighlight();
        }

        function clearLayers(): void {
            parcelsLayer?.clearLayers();
            buildingsLayer?.clearLayers();
        }

        // A click another tool of the map has handled already (the measurement of the zoning plan map) opens
        // no detail.
        function handleMapClick(e: L.LeafletMouseEvent): void {
            if (!map || e.originalEvent.defaultPrevented) {
                return;
            }

            const latlng = e.latlng;

            const buildingHit = isBuildingsVisible() ? findHitFeature(buildingsLayer, latlng) : null;

            if (buildingHit) {
                const hitProps = buildingHit.feature?.properties;

                if (hitProps) {
                    highlightFeature(buildingHit as L.Path, hitProps.id, 'building');
                    const popup = L.popup({className: 'cadastral-popup'}).setLatLng(latlng).setContent(
                        '<div class="text-center"><div class="spinner-border spinner-border-sm" role="status"></div></div>',
                    ).openOn(map);
                    popup.on('remove', () => clearHighlight());

                    const parcelHitUnder = isParcelsVisible() ? findHitFeature(parcelsLayer, latlng) : null;
                    const parcelIdUnder = parcelHitUnder?.feature?.properties?.id ?? null;

                    loadBuildingPopupForPopup(hitProps.id, parcelIdUnder, popup, latlng);
                }

                return;
            }

            const parcelHit = isParcelsVisible() ? findHitFeature(parcelsLayer, latlng) : null;

            if (parcelHit) {
                const hitProps = parcelHit.feature?.properties;

                if (hitProps) {
                    highlightFeature(parcelHit as L.Path, hitProps.id, 'parcel');
                    const popup = L.popup({className: 'cadastral-popup'}).setLatLng(latlng).setContent(
                        '<div class="text-center"><div class="spinner-border spinner-border-sm" role="status"></div></div>',
                    ).openOn(map);
                    popup.on('remove', () => clearHighlight());
                    loadParcelPopupForPopup(hitProps.id, popup, latlng);
                }
            }
        }

        function highlightFeature(
            layer: L.Path, featureId: string, featureType: 'parcel' | 'building',
        ): void {
            clearHighlight();
            highlightedLayer = layer;
            highlightedFeatureId = featureId;
            highlightedFeatureType = featureType;
            layer.setStyle(HIGHLIGHT_STYLE);
            layer.bringToFront();
        }

        function clearHighlight(): void {
            if (highlightedLayer) {
                const style = highlightedFeatureType === 'building'
                    ? BUILDING_STYLE
                    : PARCEL_STYLE;
                highlightedLayer.setStyle(style);
            }
            highlightedLayer = null;
            highlightedFeatureId = null;
            highlightedFeatureType = null;
        }

        function reapplyHighlight(): void {
            if (!highlightedFeatureId || !highlightedFeatureType) {
                return;
            }

            const targetLayer = highlightedFeatureType === 'building' ? buildingsLayer : parcelsLayer;
            if (!targetLayer) {
                return;
            }

            const id = highlightedFeatureId;
            const type = highlightedFeatureType;

            targetLayer.eachLayer((layer: any) => {
                if (layer.feature?.properties?.id === id) {
                    highlightedLayer = layer;
                    highlightedFeatureId = id;
                    highlightedFeatureType = type;
                    layer.setStyle(HIGHLIGHT_STYLE);
                    layer.bringToFront();
                }
            });
        }

        function findHitFeature(
            geoJsonLayer: L.GeoJSON | null, latlng: L.LatLng,
        ): L.Layer & {feature?: GeoJSON.Feature} | null {
            if (!geoJsonLayer) {
                return null;
            }

            let hit: (L.Layer & {feature?: GeoJSON.Feature}) | null = null;

            geoJsonLayer.eachLayer((layer: any) => {
                if (hit) {
                    return;
                }

                if (layer.getBounds && layer.getBounds().contains(latlng)) {
                    if (isPointInLayer(layer, latlng)) {
                        hit = layer;
                    }
                }
            });

            return hit;
        }

        function isPointInLayer(layer: L.Layer, latlng: L.LatLng): boolean {
            if (!map) {
                return false;
            }

            const point = map.latLngToLayerPoint(latlng);

            if ((layer as any)._containsPoint) {
                return (layer as any)._containsPoint(point);
            }

            return false;
        }

        async function loadBuildingPopupForPopup(
            buildingId: string,
            parcelId: string | null,
            popup: L.Popup,
            latlng: L.LatLng,
        ): Promise<void> {
            try {
                const promises: [Promise<BuildingObjectDetail>, Promise<ParcelDetail | null>] = [
                    fetchBuildingObjectDetail(buildingId),
                    parcelId !== null ? fetchParcelDetail(parcelId) : Promise.resolve(null),
                ];

                const [buildingDetail, parcelDetail] = await Promise.all(promises);

                const sections = buildBuildingSections(buildingDetail);
                if (parcelDetail !== null) {
                    sections.push(...buildParcelSections(parcelDetail, buildingDetail));
                }
                setPopupContentWithPanorama(
                    popup, sections, parcelDetail?.id ?? null, latlng,
                );
            } catch (error) {
                popup.setContent('<div class="text-danger">Nepodařilo se načíst detail</div>');
                log(error instanceof Error ? error.message : String(error));
            }
        }

        async function loadParcelPopupForPopup(
            parcelId: string,
            popup: L.Popup,
            latlng: L.LatLng,
        ): Promise<void> {
            try {
                const detail = await fetchParcelDetail(parcelId);
                const sections = buildParcelSections(detail, null);
                setPopupContentWithPanorama(
                    popup, sections, detail.id, latlng,
                );
            } catch (error) {
                popup.setContent(
                    '<div class="text-danger">Nepodařilo se načíst detail parcely</div>',
                );
                log('CadastralMapLayer: Parcel detail error for id='
                    + parcelId + ': '
                    + (error instanceof Error ? error.message : String(error)));
            }
        }

        function setPopupContentWithPanorama(
            popup: L.Popup,
            sections: PopupSections,
            cuzkParcelId: string | null,
            latlng: L.LatLng,
        ): void {
            const container = document.createElement('div');
            container.innerHTML = renderPopup(sections, cuzkParcelId);

            const panoramaBtn = container.querySelector('.cadastral-panorama-btn');
            if (panoramaBtn) {
                panoramaBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    openPanoramaModal(latlng);
                });
            }

            popup.setContent(container);
        }

        function openPanoramaModal(latlng: L.LatLng): void {
            document.getElementById('cadastral-panorama-modal')?.remove();

            const modalEl = document.createElement('div');
            modalEl.id = 'cadastral-panorama-modal';
            modalEl.className = 'modal fade';
            modalEl.setAttribute('tabindex', '-1');
            modalEl.innerHTML = `
                <div class="modal-dialog modal-xl modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            <span class="modal-title">Panorama</span>
                            <button type="button" class="btn-close"
                                    data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body p-0">
                            <div class="vue-mapy-cz-panorama"
                                 style="height: 70vh"
                                 :longitude="${latlng.lng}"
                                 :latitude="${latlng.lat}">
                            </div>
                        </div>
                    </div>
                </div>
            `;

            document.body.appendChild(modalEl);
            bindInemComponents(modalEl);

            const modal = new Modal(modalEl);
            modal.show();

            modalEl.addEventListener('hidden.bs.modal', () => {
                modal.dispose();
                modalEl.remove();
            });
        }

        function buildBuildingSections(detail: BuildingObjectDetail): PopupSections {
            const title = detail.houseNumber !== null
                ? `Stavební objekt č.p. ${detail.houseNumber}`
                : 'Stavební objekt';

            const rows: Array<{label: string; value: string}> = [];

            if (detail.construction !== null) {
                rows.push({label: 'Konstrukce', value: detail.construction});
            }
            if (detail.numberOfFloors !== null) {
                rows.push({label: 'Počet podlaží', value: String(detail.numberOfFloors)});
            }
            if (detail.numberOfFlats !== null) {
                rows.push({label: 'Počet bytů', value: String(detail.numberOfFlats)});
            }
            if (detail.floorArea !== null) {
                rows.push({label: 'Podlahová plocha', value: `${formatNumber(detail.floorArea)} m²`});
            }
            if (detail.buildUpArea !== null) {
                rows.push({label: 'Zastavěná plocha', value: `${formatNumber(detail.buildUpArea)} m²`});
            }
            if (detail.enclosedArea !== null) {
                rows.push({label: 'Obestavěný prostor', value: `${formatNumber(detail.enclosedArea)} m³`});
            }
            if (detail.completionDate !== null) {
                rows.push({label: 'Dokončení', value: formatDate(detail.completionDate)});
            }
            if (detail.water !== null) {
                rows.push({label: 'Voda', value: detail.water});
            }
            if (detail.gas !== null) {
                rows.push({label: 'Plyn', value: detail.gas});
            }
            if (detail.sewage !== null) {
                rows.push({label: 'Kanalizace', value: detail.sewage});
            }
            if (detail.heating !== null) {
                rows.push({label: 'Topení', value: detail.heating});
            }
            if (detail.elevator !== null) {
                rows.push({label: 'Výtah', value: detail.elevator ? 'Ano' : 'Ne'});
            }

            return [{type: 'header', title, rows}];
        }

        function buildParcelSections(
            detail: ParcelDetail, buildingDetail: BuildingObjectDetail | null,
        ): PopupSections {
            const sections: PopupSections = [];

            sections.push({
                type: 'header',
                title: 'Informace o pozemku',
                rows: [
                    {label: 'Parcelní číslo', value: detail.number},
                    {label: 'Výměra', value: `${formatNumber(detail.area)} m²`},
                    {label: 'Druh pozemku', value: detail.landType},
                ],
            });

            const addressPlaces: ParcelDetailAddressPlace[] = Array.isArray(detail.addressPlaces)
                ? detail.addressPlaces
                : Object.values(detail.addressPlaces) as ParcelDetailAddressPlace[];

            const groups = groupAddressPlaces(addressPlaces);
            const bd = buildingDetail;

            for (const group of groups) {
                const apRows: Array<{label: string; value: string}> = [];
                const houseNumbers = group
                    .map((g) => g.houseNumber)
                    .filter((n): n is number => n !== null)
                    .sort((a, b) => a - b);
                const evidenceNumbers = group
                    .map((g) => g.evidenceNumber)
                    .filter((n): n is number => n !== null)
                    .sort((a, b) => a - b);

                if (houseNumbers.length > 0) {
                    apRows.push({
                        label: houseNumbers.length > 1 ? 'Stavební objekty' : 'Stavební objekt',
                        value: houseNumbers.map((n) => `č.p. ${n}`).join(', '),
                    });
                }
                if (evidenceNumbers.length > 0) {
                    apRows.push({
                        label: 'Číslo evidenční',
                        value: evidenceNumbers.join(', '),
                    });
                }

                const ap = group[0];
                if (ap.estateType !== null) {
                    apRows.push({label: 'Typ stavby', value: ap.estateType});
                }
                if (ap.construction !== null && !(bd && bd.construction !== null)) {
                    apRows.push({label: 'Konstrukce', value: ap.construction});
                }
                if (ap.numberOfFloors !== null && !(bd && bd.numberOfFloors !== null)) {
                    apRows.push({label: 'Počet podlaží', value: String(ap.numberOfFloors)});
                }
                if (ap.floorArea !== null && !(bd && bd.floorArea !== null)) {
                    apRows.push({label: 'Podlahová plocha', value: `${formatNumber(ap.floorArea)} m²`});
                }
                if (ap.buildUpArea !== null && !(bd && bd.buildUpArea !== null)) {
                    apRows.push({label: 'Zastavěná plocha', value: `${formatNumber(ap.buildUpArea)} m²`});
                }
                if (ap.constructionDate !== null) {
                    apRows.push({label: 'Datum výstavby', value: formatDate(ap.constructionDate)});
                }
                if (ap.completionDate !== null && !(bd && bd.completionDate !== null)) {
                    apRows.push({label: 'Dokončení', value: formatDate(ap.completionDate)});
                }
                if (ap.water !== null && !(bd && bd.water !== null)) {
                    apRows.push({label: 'Voda', value: ap.water});
                }
                if (ap.gas !== null && !(bd && bd.gas !== null)) {
                    apRows.push({label: 'Plyn', value: ap.gas});
                }
                if (ap.sewage !== null && !(bd && bd.sewage !== null)) {
                    apRows.push({label: 'Kanalizace', value: ap.sewage});
                }
                if (ap.heating !== null && !(bd && bd.heating !== null)) {
                    apRows.push({label: 'Topení', value: ap.heating});
                }

                if (apRows.length > 0) {
                    const title = houseNumbers.length > 1
                        ? 'Součástí jsou stavby'
                        : 'Součástí je stavba';
                    sections.push({type: 'subheader', title, rows: apRows});
                }
            }

            return sections;
        }

        function groupAddressPlaces(
            addressPlaces: ParcelDetailAddressPlace[],
        ): ParcelDetailAddressPlace[][] {
            const groups: ParcelDetailAddressPlace[][] = [];
            const keyToGroup = new Map<string, ParcelDetailAddressPlace[]>();

            for (const ap of addressPlaces) {
                const key = JSON.stringify({
                    estateType: ap.estateType,
                    construction: ap.construction,
                    numberOfFloors: ap.numberOfFloors,
                    floorArea: ap.floorArea,
                    buildUpArea: ap.buildUpArea,
                    constructionDate: ap.constructionDate,
                    completionDate: ap.completionDate,
                    water: ap.water,
                    gas: ap.gas,
                    sewage: ap.sewage,
                    heating: ap.heating,
                });

                const existing = keyToGroup.get(key);
                if (existing) {
                    existing.push(ap);
                } else {
                    const group = [ap];
                    keyToGroup.set(key, group);
                    groups.push(group);
                }
            }

            return groups;
        }

        function renderPopup(sections: PopupSections, cuzkParcelId: string | null): string {
            let html = '<div class="popup-content">';
            html += '<table class="popup-table">';

            for (let i = 0; i < sections.length; i++) {
                const section = sections[i];
                const isFirst = i === 0;

                if (section.type === 'header') {
                    html += `<tr class="popup-header"><td colspan="2"${isFirst ? ' class="first"' : ''}>`
                        + `${escapeHtml(section.title)}</td></tr>`;
                } else {
                    html += `<tr class="popup-subheader"><td colspan="2">`
                        + `${escapeHtml(section.title)}</td></tr>`;
                }

                for (let j = 0; j < section.rows.length; j++) {
                    const row = section.rows[j];
                    const firstClass = j === 0 ? ' first' : '';
                    html += '<tr>'
                        + `<td class="popup-label${firstClass}">${escapeHtml(row.label)}</td>`
                        + `<td class="popup-value${firstClass}">${escapeHtml(row.value)}</td>`
                        + '</tr>';
                }
            }

            html += '</table>';

            html += '<div class="popup-footer">';

            html += '<a href="#" class="cadastral-panorama-btn popup-link">'
                + '<i class="fas fa-street-view me-2"></i>Panorama</a>';

            if (cuzkParcelId) {
                const numericId = cuzkParcelId.replace(/\D/g, '');
                const cuzkUrl = 'https://nahlizenidokn.cuzk.gov.cz'
                    + '/ZobrazObjekt.aspx?typ=parcela&id='
                    + encodeURIComponent(numericId);
                html += `<a href="${cuzkUrl}"`
                    + ` onclick="window.open(this.href, 'cuzk',`
                    + ` 'width=600,height=660'); return false;"`
                    + ` class="popup-link">Nahlížení do KN ›</a>`;
            }

            html += '</div>';

            html += '</div>';
            return html;
        }

        function injectLabelStyles(): void {
            if (document.getElementById('cadastral-label-styles')) {
                return;
            }

            const style = document.createElement('style');
            style.id = 'cadastral-label-styles';
            style.textContent = `
                .cadastral-label {
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    white-space: nowrap;
                }
                .cadastral-label::before {
                    display: none !important;
                }
                .cadastral-label--parcel {
                    color: #333;
                    font-size: 10px;
                    font-weight: 400;
                }
                .cadastral-label--building {
                    color: #333;
                    font-size: 10px;
                    font-weight: 600;
                    font-style: italic;
                    background: rgba(153, 153, 153, 0.35) !important;
                    padding: 0 2px !important;
                    border-radius: 2px;
                }
                .cadastral-zoom-hint {
                    background: rgba(255, 255, 255, 0.85);
                    padding: 4px 10px;
                    font-size: 12px;
                    color: #555;
                }
            `;
            document.head.appendChild(style);
        }

        function updateZoomHint(): void {
            if (!zoomHintControl) {
                return;
            }

            const container = zoomHintControl.getContainer();
            if (!container) {
                return;
            }

            const anyLayerVisible = isParcelsVisible() || isBuildingsVisible();
            const show = anyLayerVisible && map !== null && map.getZoom() < MIN_ZOOM;
            container.style.display = show ? '' : 'none';
        }

        function updateCursorClass(): void {
            if (!map) {
                return;
            }

            const container = map.getContainer();
            const active = (isParcelsVisible() || isBuildingsVisible())
                && map.getZoom() >= MIN_ZOOM;

            if (active) {
                container.classList.add('cadastral-active');
            } else {
                container.classList.remove('cadastral-active');
            }
        }

        function getGeometryBounds(geometry: GeoJSON.Geometry): L.LatLngBounds | null {
            const points: L.LatLng[] = [];

            const collect = (arr: any): void => {
                if (Array.isArray(arr) && arr.length >= 2 && typeof arr[0] === 'number') {
                    points.push(L.latLng(arr[1] as number, arr[0] as number));
                } else if (Array.isArray(arr)) {
                    for (const item of arr) {
                        collect(item);
                    }
                }
            };

            if ('coordinates' in geometry) {
                collect((geometry as any).coordinates);
            }

            return points.length > 0 ? L.latLngBounds(points) : null;
        }

        function formatDate(isoDate: string): string {
            const [year, month, day] = isoDate.split('-');
            return `${parseInt(day, 10)}. ${parseInt(month, 10)}. ${year}`;
        }

        function formatNumber(value: number): string {
            return value.toLocaleString('cs-CZ');
        }

        function escapeHtml(text: string): string {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }

        let cleanupMapListener: (() => void) | null = null;

        onMounted(() => {
            cleanupMapListener = acquireLeafletMap(rootElement.value!, attachToMap);
        });

        onBeforeUnmount(() => {
            cleanupMapListener?.();
            if (debounceTimer !== null) {
                window.clearTimeout(debounceTimer);
            }
            previousAbortController?.abort();
            detachFromMap();
        });

        return {rootElement};
    },
});
</script>
